import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import PageLayout from '../components/PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { getManagedUsers, getPropertyList, updateManagedUserProperties } from '../apiCalls';
import { isSuperAdmin } from '../utils/roleUtils';

const UserAccessManager = () => {
    const pageLayoutProps = usePageLayout();
    const { role } = pageLayoutProps;
    const { enqueueSnackbar } = useSnackbar();
    const [users, setUsers] = useState([]);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [editingUserId, setEditingUserId] = useState(null);
    const [draftProperties, setDraftProperties] = useState([]);
    const [saving, setSaving] = useState(false);
    const allowed = isSuperAdmin(role);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [userList, propertyList] = await Promise.all([getManagedUsers(), getPropertyList()]);
            setUsers(userList);
            setProperties(Array.isArray(propertyList) ? propertyList : []);
        } catch (err) {
            setError('Users could not be loaded. Please retry.');
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (allowed) load();
    }, [allowed, load]);

    const propertyNames = useMemo(
        () => new Map(properties.map((property) => [property.propertyID, property.companyName || property.propertyID])),
        [properties],
    );

    const filteredUsers = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return users;
        return users.filter((user) => `${user.name} ${user.email}`.toLowerCase().includes(term));
    }, [search, users]);

    const startEdit = (user) => {
        setEditingUserId(user.id);
        setDraftProperties(user.properties || []);
    };

    const save = async (user) => {
        if (!draftProperties.length) {
            enqueueSnackbar('Select at least one property', { variant: 'warning' });
            return;
        }
        setSaving(true);
        try {
            const updated = await updateManagedUserProperties(user.id, draftProperties);
            setUsers((current) => current.map((candidate) => (candidate.id === user.id ? { ...candidate, ...updated } : candidate)));
            setEditingUserId(null);
            enqueueSnackbar('Property access updated', { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Property access could not be updated', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setSaving(false);
        }
    };

    if (!allowed) {
        return (
            <PageLayout {...pageLayoutProps}>
                <Typography.Title level={4}>User access</Typography.Title>
                <Typography.Text type='secondary'>Super-admin access is required.</Typography.Text>
            </PageLayout>
        );
    }

    const columns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Email', dataIndex: 'email', key: 'email' },
        { title: 'Role', dataIndex: 'role', key: 'role', render: (value) => <Tag>{value}</Tag> },
        {
            title: 'Properties',
            key: 'properties',
            render: (_, user) => (editingUserId === user.id ? (
                <Select
                    mode='multiple'
                    aria-label={`Properties for ${user.email}`}
                    style={{ minWidth: 280, width: '100%' }}
                    value={draftProperties}
                    onChange={setDraftProperties}
                    optionFilterProp='label'
                    options={properties.map((property) => ({ value: property.propertyID, label: property.companyName || property.propertyID }))}
                />
            ) : (
                <Space size={[4, 4]} wrap>
                    {(user.properties || []).map((propertyID) => <Tag key={propertyID}>{propertyNames.get(propertyID) || propertyID}</Tag>)}
                </Space>
            )),
        },
        {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, user) => (editingUserId === user.id ? (
                <Space>
                    <Button type='primary' loading={saving} onClick={() => save(user)}>Save</Button>
                    <Button disabled={saving} onClick={() => setEditingUserId(null)}>Cancel</Button>
                </Space>
            ) : (
                <Button disabled={Boolean(editingUserId)} onClick={() => startEdit(user)}>Edit access</Button>
            )),
        },
    ];

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: 1280, margin: '0 auto' }}>
                <Space direction='vertical' size={6} style={{ marginBottom: 24 }}>
                    <Typography.Title level={2} style={{ margin: 0 }}>User access</Typography.Title>
                    <Typography.Paragraph type='secondary' style={{ margin: 0 }}>
                        Choose which properties each user can access. Changes apply on the user&apos;s next request and are audited.
                    </Typography.Paragraph>
                </Space>
                {error ? <Alert type='warning' showIcon message={error} style={{ marginBottom: 16 }} /> : null}
                <Card>
                    <Input.Search
                        placeholder='Search by name or email'
                        allowClear
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        style={{ maxWidth: 360, marginBottom: 16 }}
                    />
                    <Table rowKey='id' loading={loading} columns={columns} dataSource={filteredUsers} pagination={{ pageSize: 25 }} />
                </Card>
            </div>
        </PageLayout>
    );
};

export default UserAccessManager;
